<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_TEAMS } from '@/services/mockData';
import { playersApi, lineupsApi } from '@/services/api';
import type { PlayerProfile, PlayerPosition } from '@/types/models';
import BaseballDiamond from '@/components/lineup/BaseballDiamond.vue';
import OfficialLineupCardModal from '@/components/lineup/OfficialLineupCardModal.vue';
import AppBadge from '@/components/ui/AppBadge.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NSelect, NTag } from 'naive-ui';
import {
  Trophy,
  Save,
  RotateCcw,
  Sparkles,
  ArrowDownUp,
  UserCheck,
  Printer,
  Shield,
  Layers,
  Info,
  RefreshCw
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant, managedTeams, currentUser } = useTenant();
const { notifySuccess, notifyInfo } = useToastNotification();

const isCoach = computed(() =>
  currentUser.value.role === 'ADMIN' ||
  currentUser.value.role === 'COACH' ||
  currentUser.value.role === 'HEAD_COACH' ||
  currentUser.value.role === 'ORG_ADMIN'
);
const currentTeam = computed(() => managedTeams.value.find(t => t.tenantId === activeTenant.value.id) || managedTeams.value[0] || INITIAL_TEAMS[0]);

const availablePlayers = ref<PlayerProfile[]>([]);
const isLoading = ref(false);

const loadData = async () => {
  isLoading.value = true;
  try {
    const list = await playersApi.getPlayers(currentTeam.value.id, activeTenant.value.id);
    availablePlayers.value = list || [];
    if (list && list.length >= 9) {
      battingOrder.value.forEach((slot, idx) => {
        if (!slot.player && list[idx]) {
          slot.player = list[idx];
          slot.pos = list[idx].position || 'DH';
        }
      });
    }
  } catch (err) {
    console.warn('[LineupView] Error loading players:', err);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadData();
});

watch(() => activeTenant.value.id, () => {
  loadData();
});

// Defensive Shift Presets
const shiftPreset = ref<'STANDARD' | 'INFIELD_IN' | 'PULL_SHIFT' | 'NO_DOUBLES'>('STANDARD');
const isPrintModalOpen = ref(false);
const activeSelectedFieldPos = ref<PlayerPosition | null>(null);

// Batting order (1 to 9)
const battingOrder = ref<Array<{ slot: number; player: PlayerProfile | null; pos: PlayerPosition }>>([
  { slot: 1, player: null, pos: 'CF' },
  { slot: 2, player: null, pos: 'SS' },
  { slot: 3, player: null, pos: 'C' },
  { slot: 4, player: null, pos: '1B' },
  { slot: 5, player: null, pos: 'RF' },
  { slot: 6, player: null, pos: '3B' },
  { slot: 7, player: null, pos: '2B' },
  { slot: 8, player: null, pos: 'LF' },
  { slot: 9, player: null, pos: 'P' },
]);

// Defensive field assignments map
const defensiveAssignments = computed<Record<PlayerPosition, PlayerProfile | null>>(() => {
  const map: Record<PlayerPosition, PlayerProfile | null> = {
    P: null, C: null, '1B': null, '2B': null, '3B': null,
    SS: null, LF: null, CF: null, RF: null, DH: null, UTIL: null
  };

  battingOrder.value.forEach(item => {
    if (item.player && item.pos) {
      map[item.pos] = item.player;
    }
  });

  return map;
});

const handlePositionClick = (pos: PlayerPosition) => {
  activeSelectedFieldPos.value = pos;
  const currentSlot = battingOrder.value.find(s => s.pos === pos);
  if (currentSlot && currentSlot.player) {
    notifyInfo(`Position: ${pos}`, `#${currentSlot.player.jerseyNumber} ${currentSlot.player.firstName} ${currentSlot.player.lastName}`);
  }
};

const handleSaveLineup = async () => {
  try {
    await lineupsApi.saveLineup(currentTeam.value.id, {
      battingOrder: battingOrder.value,
      shiftPreset: shiftPreset.value,
    });
    notifySuccess(t('lineup.cardSaved'), t('lineup.cardSavedMsg'));
  } catch (err: any) {
    notifyError('Save Lineup Error', err?.message || 'Could not save lineup card.');
  }
};

const handleAutoFill = () => {
  const active = availablePlayers.value.filter(p => p.status === 'ACTIVE');
  const posPreferences: PlayerPosition[] = ['CF', 'SS', 'C', '1B', 'RF', '3B', '2B', 'LF', 'P'];
  
  battingOrder.value.forEach((slot, idx) => {
    if (active[idx]) {
      slot.player = active[idx];
      slot.pos = active[idx].position || posPreferences[idx] || 'DH';
    }
  });
  notifySuccess(t('lineup.autoAssigned'), t('lineup.autoAssignedMsg'));
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ t('lineup.title') }}
          </h1>
          <AppBadge variant="primary">{{ t('lineup.badge') }}</AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('lineup.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2.5">
        <NButton
          secondary
          @click="loadData"
          :loading="isLoading"
          class="font-semibold text-xs"
          title="Refresh Lineup"
        >
          <template #icon>
            <RefreshCw class="w-4 h-4 text-sky-400" />
          </template>
          Sync
        </NButton>

        <!-- Print Lineup Card Trigger -->
        <NButton
          secondary
          @click="isPrintModalOpen = true"
          class="font-semibold text-xs"
        >
          <template #icon>
            <Printer class="w-4 h-4 text-sky-400" />
          </template>
          Print Lineup
        </NButton>

        <template v-if="isCoach">
          <NButton
            secondary
            @click="handleAutoFill"
            class="font-semibold text-xs"
          >
            <template #icon>
              <Sparkles class="w-4 h-4 text-amber-400" />
            </template>
            {{ t('lineup.autoFill') }}
          </NButton>

          <NButton
            type="primary"
            @click="handleSaveLineup"
            class="font-bold text-xs shadow-lg shadow-sky-500/20"
          >
            <template #icon>
              <Save class="w-4 h-4" />
            </template>
            {{ t('lineup.saveCard') }}
          </NButton>
        </template>
      </div>
    </div>

    <!-- Defensive Shift Quick Bar -->
    <div class="p-3 bg-slate-900 border border-slate-800 rounded-2xl flex flex-wrap items-center justify-between gap-3 shadow-md">
      <div class="flex items-center gap-2 pl-1">
        <Layers class="w-4 h-4 text-sky-400" />
        <span class="text-xs font-bold text-slate-300">{{ t('lineup.shiftScheme') }}</span>
      </div>

      <div class="flex items-center gap-1.5">
        <button
          v-for="preset in [
            { id: 'STANDARD', label: t('lineup.shiftStandard') },
            { id: 'INFIELD_IN', label: t('lineup.shiftInfieldIn') },
            { id: 'PULL_SHIFT', label: t('lineup.shiftPull') },
            { id: 'NO_DOUBLES', label: t('lineup.shiftNoDoubles') },
          ]"
          :key="preset.id"
          type="button"
          @click="shiftPreset = preset.id as any"
          :class="[
            'px-3 py-1.5 rounded-xl text-xs font-bold transition',
            shiftPreset === preset.id
              ? 'bg-sky-500 text-slate-950 font-extrabold shadow-sm'
              : 'bg-slate-950 text-slate-400 border border-slate-800 hover:text-slate-200 hover:bg-slate-800'
          ]"
        >
          {{ preset.label }}
        </button>
      </div>
    </div>

    <!-- Grid Layout: Batting Order (Col 1) & Defensive Field (Col 2) -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      <!-- 9-Hole Batting Order Card (5 cols) -->
      <div class="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl space-y-4">
        <div class="flex items-center justify-between pb-3 border-b border-slate-800">
          <h3 class="text-sm font-bold text-slate-100 uppercase tracking-wider flex items-center gap-2">
            <ArrowDownUp class="w-4 h-4 text-sky-400" />
            {{ t('lineup.battingOrder') }}
          </h3>
          <span class="text-xs text-slate-400">{{ t('lineup.starters') }}</span>
        </div>

        <div class="space-y-2.5">
          <div
            v-for="item in battingOrder"
            :key="item.slot"
            class="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-950 border border-slate-800/80 hover:border-slate-700 transition"
          >
            <!-- Slot number badge -->
            <span class="w-6 h-6 rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-400 font-mono font-extrabold text-xs flex items-center justify-center shrink-0">
              {{ item.slot }}
            </span>

            <!-- Read-only or Editable Player select -->
            <template v-if="isCoach">
              <select
                v-model="item.player"
                class="flex-1 bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              >
                <option :value="null">{{ t('lineup.selectBatter') }}</option>
                <option v-for="p in availablePlayers" :key="p.id" :value="p">
                  #{{ p.jerseyNumber }} {{ p.lastName }} {{ p.firstName }} ({{ p.position }} - {{ p.bats }}/{{ p.throws }})
                </option>
              </select>

              <select
                v-model="item.pos"
                class="w-20 bg-slate-900 border border-slate-800 rounded-lg px-2 py-1.5 text-xs font-mono font-bold text-sky-400 focus:outline-none focus:border-sky-500"
              >
                <option value="P">P</option>
                <option value="C">C</option>
                <option value="1B">1B</option>
                <option value="2B">2B</option>
                <option value="3B">3B</option>
                <option value="SS">SS</option>
                <option value="LF">LF</option>
                <option value="CF">CF</option>
                <option value="RF">RF</option>
                <option value="DH">DH</option>
              </select>
            </template>

            <template v-else>
              <div class="flex-1 flex items-center justify-between px-2">
                <span class="text-xs font-bold text-slate-200">
                  {{ item.player ? `#${item.player.jerseyNumber} ${item.player.lastName} ${item.player.firstName}` : 'TBD' }}
                </span>
                <span class="text-xs font-mono font-bold text-sky-400">{{ item.pos }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Defensive Field Diagram (7 cols) -->
      <div class="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
        <div class="flex items-center justify-between pb-2 border-b border-slate-800">
          <h3 class="text-sm font-bold text-slate-100 uppercase tracking-wider">
            {{ t('lineup.defensivePos') }}
          </h3>
          <span class="text-xs text-emerald-400 font-semibold flex items-center gap-1">
            <UserCheck class="w-3.5 h-3.5" /> {{ t('lineup.synchronized') }}
          </span>
        </div>

        <BaseballDiamond
          :assignments="defensiveAssignments"
          :selected-position="activeSelectedFieldPos"
          :shift-preset="shiftPreset"
          @select-position="handlePositionClick"
        />

        <p class="text-center text-[11px] text-slate-500 pt-1">
          {{ t('lineup.fieldHelp') }}
        </p>
      </div>
    </div>

    <!-- Official Umpire Lineup Card Modal -->
    <OfficialLineupCardModal
      v-model="isPrintModalOpen"
      :team="currentTeam"
      :batting-order="battingOrder"
      :starting-pitcher="defensiveAssignments.P"
    />
  </div>
</template>
